import { useState, FormEvent } from "react";
import { useNavigate } from "react-router-dom";
import { useTheme } from "../../../contexts/ThemeContext";
import { useAuth } from "../../../contexts/AuthContext";

const SUPABASE_FUNCTIONS_URL = import.meta.env.VITE_PUBLIC_SUPABASE_URL
  ? `${import.meta.env.VITE_PUBLIC_SUPABASE_URL}/functions/v1`
  : "";

const MAILERLITE_ACTION =
  "https://assets.mailerlite.com/jsonp/2208570/forms/182464527424554064/subscribe";

const FREE_FEATURES = [
  "Full profit calculator",
  "All platform fee structures",
  "Unlimited flip calculations",
  "Basic flip history log",
  "Early access community",
];

const PRO_FEATURES = [
  "Everything in Early Access",
  "Daily performance dashboard",
  "Full flip history & log",
  "ROI analytics over time",
  "Advanced analytics & trends",
  "Custom fee overrides",
  "Multi-user access",
  "Bulk flip import",
  "CSV export",
  "Priority & dedicated support",
];

export default function PricingSection() {
  const [email, setEmail] = useState("");
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [checkoutLoading, setCheckoutLoading] = useState<string | null>(null);
  const { isDark } = useTheme();
  const { user } = useAuth();
  const navigate = useNavigate();

  const handleEarlyAccess = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!email.trim()) return;
    setSubmitting(true);
    try {
      const body = new URLSearchParams();
      body.append("fields[email]", email.trim());
      body.append("ml-submit", "1");
      body.append("anticsrf", "true");
      await fetch(MAILERLITE_ACTION, {
        method: "POST",
        mode: "no-cors",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: body.toString(),
      });
      setSubmitted(true);
    } catch {
      setSubmitted(true);
    } finally {
      setSubmitting(false);
    }
  };

  const handleProCheckout = async (plan: "pro_monthly" | "pro_yearly" | "pro_lifetime") => {
    if (plan === "pro_monthly") {
      window.location.href = "https://buy.stripe.com/fZu6oA4CVdaZ47N35K2ZO00";
      return;
    }

    if (plan === "pro_yearly") {
      window.location.href = "https://buy.stripe.com/eVqcMY3yR1sheMrayc2ZO02";
      return;
    }

    if (plan === "pro_lifetime") {
      window.location.href = "https://buy.stripe.com/fZu8wIglDdaZ9s77m02ZO01";
      return;
    }

    if (!user) {
      sessionStorage.setItem("trackly_pro_intent", "lifetime");
      navigate(`/dashboard?intent=pro`);
      return;
    }

    setCheckoutLoading(plan);
    try {
      const origin = window.location.origin;
      const res = await fetch(`${SUPABASE_FUNCTIONS_URL}/stripe-checkout`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          plan,
          successUrl: `${origin}/dashboard?checkout=success&session={CHECKOUT_SESSION_ID}`,
          cancelUrl: `${origin}/product#pricing`,
        }),
      });
      const data = await res.json();
      if (data.url) {
        window.location.href = data.url;
      }
    } catch {
      // silently fail
    } finally {
      setCheckoutLoading(null);
    }
  };

  return (
    <section id="pricing" className={`py-24 px-8 lg:px-16 border-t transition-colors duration-300 ${isDark ? "bg-[#050505] border-white/5" : "bg-white border-black/5"}`}>
      <div className="max-w-7xl mx-auto">
        <div className="text-center mb-14">
          <span className={`font-inter text-xs uppercase tracking-[0.2em] block mb-4 ${isDark ? "text-[#666666]" : "text-[#888888]"}`}>
            Pricing
          </span>
          <h2 className={`font-syne font-bold text-5xl lg:text-6xl mb-4 ${isDark ? "text-white" : "text-[#111111]"}`}>
            Start free. <br />
            <span className={isDark ? "text-[#444444]" : "text-[#cccccc]"}>Upgrade when you&apos;re ready.</span>
          </h2>
          <p className={`font-inter max-w-md mx-auto text-base ${isDark ? "text-[#555555]" : "text-[#666666]"}`}>
            Trackly is in early access. Join now for free and be first to use it when we launch.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-3xl mx-auto">
          {/* Free / Early Access Card */}
          <div className={`rounded-2xl p-8 flex flex-col border transition-all duration-300 cursor-default ${isDark ? "bg-[#0d0d0d] border-white/5 hover:border-white/10" : "bg-[#f8f8f8] border-black/5 hover:border-black/10"}`}>
            <div className="mb-6">
              <p className={`font-inter text-xs uppercase tracking-[0.15em] mb-3 ${isDark ? "text-[#666666]" : "text-[#888888]"}`}>
                Early Access
              </p>
              <div className="flex items-baseline gap-0.5 mb-3">
                <span className={`font-syne font-bold text-4xl ${isDark ? "text-white" : "text-[#111111]"}`}>Free</span>
              </div>
              <p className={`font-inter text-sm leading-relaxed ${isDark ? "text-[#555555]" : "text-[#666666]"}`}>
                Be first in line. Lock in free access to Trackly before we launch publicly.
              </p>
            </div>

            <ul className="flex flex-col gap-3 mb-8 flex-1">
              {FREE_FEATURES.map((f) => (
                <li key={f} className="flex items-center gap-2.5">
                  <div className="w-4 h-4 flex items-center justify-center flex-shrink-0">
                    <i className={`ri-check-line text-sm ${isDark ? "text-[#666666]" : "text-[#888888]"}`} />
                  </div>
                  <span className={`font-inter text-sm ${isDark ? "text-[#555555]" : "text-[#666666]"}`}>{f}</span>
                </li>
              ))}
            </ul>

            {submitted ? (
              <div className={`w-full border rounded-full py-3 text-center ${isDark ? "bg-white/5 border-white/10" : "bg-black/4 border-black/10"}`}>
                <span className={`font-inter text-sm ${isDark ? "text-[#888888]" : "text-[#666666]"}`}>You&apos;re on the list!</span>
              </div>
            ) : (
              <form
                data-readdy-form
                id="early-access-pricing-form"
                action={MAILERLITE_ACTION}
                method="post"
                onSubmit={handleEarlyAccess}
                className="flex flex-col gap-2"
              >
                <input
                  type="email"
                  name="fields[email]"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="your@email.com"
                  className={`w-full border rounded-xl px-4 py-2.5 font-inter text-sm focus:outline-none transition-colors ${
                    isDark
                      ? "bg-white/5 border-white/10 text-white placeholder:text-[#555555] focus:border-white/25"
                      : "bg-white border-black/10 text-[#111111] placeholder:text-[#aaaaaa] focus:border-black/25"
                  }`}
                />
                <input type="hidden" name="ml-submit" value="1" />
                <input type="hidden" name="anticsrf" value="true" />
                <button
                  type="submit"
                  disabled={submitting}
                  className={`w-full font-inter font-medium text-sm py-2.5 rounded-xl transition-all duration-200 cursor-pointer whitespace-nowrap ${
                    isDark
                      ? "bg-white/10 border border-white/15 text-white hover:bg-white/15"
                      : "bg-black/8 border border-black/12 text-[#111111] hover:bg-black/12"
                  }`}
                >
                  {submitting ? "Joining..." : "Get Early Access"}
                </button>
              </form>
            )}
          </div>

          {/* Pro Card */}
          <div className={`rounded-2xl p-8 flex flex-col border transition-all duration-300 cursor-default ${isDark ? "bg-white text-black border-transparent" : "bg-[#111111] text-white border-transparent"}`}>
            <div className="mb-6">
              <div className="flex items-center justify-between mb-5">
                <p className={`font-inter text-xs uppercase tracking-[0.15em] ${isDark ? "text-black/50" : "text-white/50"}`}>
                  Pro
                </p>
                <span className={`font-inter text-[10px] uppercase tracking-widest px-2.5 py-1 rounded-full ${isDark ? "bg-black/8 text-black/50" : "bg-white/10 text-white/50"}`}>
                  Most Popular
                </span>
              </div>
              <p className={`font-inter text-sm leading-relaxed ${isDark ? "text-black/60" : "text-white/70"}`}>
                For serious resellers who want full tracking, analytics, and every tool we offer.
              </p>
            </div>

            <ul className="flex flex-col gap-3 mb-8 flex-1">
              {PRO_FEATURES.map((f) => (
                <li key={f} className="flex items-center gap-2.5">
                  <div className="w-4 h-4 flex items-center justify-center flex-shrink-0">
                    <i className={`ri-check-line text-sm ${isDark ? "text-black/70" : "text-white/70"}`} />
                  </div>
                  <span className={`font-inter text-sm ${isDark ? "text-black/80" : "text-white/80"}`}>{f}</span>
                </li>
              ))}
            </ul>

            {/* Two plan options */}
            <div id="pricing-plans" className="flex flex-col gap-3">
              {/* Monthly */}
              <button
                type="button"
                onClick={() => handleProCheckout("pro_monthly")}
                disabled={checkoutLoading !== null}
                className={`w-full group flex items-center justify-between px-5 py-4 rounded-xl border transition-all duration-200 cursor-pointer whitespace-nowrap disabled:opacity-50 disabled:cursor-not-allowed ${
                  isDark
                    ? "bg-black/6 border-black/10 hover:bg-black/12 hover:border-black/20"
                    : "bg-white/6 border-white/12 hover:bg-white/12 hover:border-white/20"
                }`}
              >
                <div className="text-left">
                  <p className={`font-inter font-semibold text-sm ${isDark ? "text-black" : "text-white"}`}>
                    Pro Monthly
                  </p>
                  <p className={`font-inter text-xs mt-0.5 ${isDark ? "text-black/45" : "text-white/45"}`}>
                    Billed monthly, cancel anytime
                  </p>
                </div>
                <div className="flex items-center gap-3 flex-shrink-0">
                  <div className="text-right">
                    <span className={`font-syne font-bold text-xl ${isDark ? "text-black" : "text-white"}`}>$10</span>
                    <span className={`font-inter text-xs ml-0.5 ${isDark ? "text-black/45" : "text-white/45"}`}>/mo</span>
                  </div>
                  {checkoutLoading === "pro_monthly" ? (
                    <div className={`w-7 h-7 flex items-center justify-center rounded-full flex-shrink-0 ${isDark ? "bg-black text-white" : "bg-white text-black"}`}>
                      <i className="ri-loader-4-line animate-spin text-xs" />
                    </div>
                  ) : null}
                </div>
              </button>

              {/* Yearly */}
              <button
                type="button"
                onClick={() => handleProCheckout("pro_yearly")}
                disabled={checkoutLoading !== null}
                className={`w-full group flex items-center justify-between px-5 py-4 rounded-xl border transition-all duration-200 cursor-pointer whitespace-nowrap disabled:opacity-50 disabled:cursor-not-allowed ${
                  isDark
                    ? "bg-black/6 border-black/10 hover:bg-black/12 hover:border-black/20"
                    : "bg-white/6 border-white/12 hover:bg-white/12 hover:border-white/20"
                }`}
              >
                <div className="text-left">
                  <div className="flex items-center gap-2">
                    <p className={`font-inter font-semibold text-sm ${isDark ? "text-black" : "text-white"}`}>
                      Pro Yearly
                    </p>
                    <span className={`font-inter text-[9px] uppercase tracking-wider px-1.5 py-0.5 rounded-full flex-shrink-0 ${isDark ? "bg-black/10 text-black/55" : "bg-white/15 text-white/65"}`}>
                      2 months free
                    </span>
                  </div>
                  <p className={`font-inter text-xs mt-0.5 ${isDark ? "text-black/45" : "text-white/45"}`}>
                    Billed annually — save $20
                  </p>
                </div>
                <div className="flex items-center gap-3 flex-shrink-0">
                  <div className="text-right">
                    <div className="flex items-baseline gap-1 justify-end">
                      <span className={`font-inter text-xs line-through ${isDark ? "text-black/30" : "text-white/30"}`}>$120</span>
                      <span className={`font-syne font-bold text-xl ${isDark ? "text-black" : "text-white"}`}>$100</span>
                    </div>
                    <span className={`font-inter text-xs ${isDark ? "text-black/45" : "text-white/45"}`}>/yr</span>
                  </div>
                  {checkoutLoading === "pro_yearly" ? (
                    <div className={`w-7 h-7 flex items-center justify-center rounded-full flex-shrink-0 ${isDark ? "bg-black text-white" : "bg-white text-black"}`}>
                      <i className="ri-loader-4-line animate-spin text-xs" />
                    </div>
                  ) : null}
                </div>
              </button>

              {/* Lifetime */}
              <button
                type="button"
                onClick={() => handleProCheckout("pro_lifetime")}
                disabled={checkoutLoading !== null}
                className={`w-full group flex items-center justify-between px-5 py-4 rounded-xl border transition-all duration-200 cursor-pointer whitespace-nowrap disabled:opacity-50 disabled:cursor-not-allowed ${
                  isDark
                    ? "bg-black text-white border-black/10 hover:bg-[#1a1a1a]"
                    : "bg-white text-black border-white/10 hover:bg-[#f5f5f5]"
                }`}
              >
                <div className="text-left">
                  <div className="flex items-center gap-2">
                    <p className={`font-inter font-semibold text-sm ${isDark ? "text-white" : "text-black"}`}>
                      Lifetime Access
                    </p>
                    <span className={`font-inter text-[9px] uppercase tracking-wider px-1.5 py-0.5 rounded-full ${isDark ? "bg-white/15 text-white/70" : "bg-black/8 text-black/50"}`}>
                      Best value
                    </span>
                  </div>
                  <p className={`font-inter text-xs mt-0.5 ${isDark ? "text-white/45" : "text-black/45"}`}>
                    Pay once, use forever
                  </p>
                </div>
                <div className="flex items-center gap-3 flex-shrink-0">
                  <div className="text-right">
                    <span className={`font-syne font-bold text-xl ${isDark ? "text-white" : "text-black"}`}>$200</span>
                    <span className={`font-inter text-xs ml-1 ${isDark ? "text-white/45" : "text-black/45"}`}>once</span>
                  </div>
                  {checkoutLoading === "pro_lifetime" ? (
                    <div className={`w-7 h-7 flex items-center justify-center rounded-full flex-shrink-0 ${isDark ? "bg-white text-black" : "bg-black text-white"}`}>
                      <i className="ri-loader-4-line animate-spin text-xs" />
                    </div>
                  ) : (
                    <div className={`w-7 h-7 flex items-center justify-center rounded-full flex-shrink-0 transition-all duration-200 group-hover:scale-110 ${isDark ? "bg-white text-black" : "bg-black text-white"}`}>
                      <i className="ri-arrow-right-line text-xs" />
                    </div>
                  )}
                </div>
              </button>
            </div>
          </div>
        </div>

        <p className={`text-center font-inter text-xs mt-8 ${isDark ? "text-[#444444]" : "text-[#aaaaaa]"}`}>
          Pricing is pre-launch and subject to change. Early access members get the best deal.
        </p>

        {/* Logo + App Store CTA */}
        <div className="flex flex-col items-center gap-6 mt-16 pt-16 border-t border-dashed" style={{ borderColor: isDark ? "rgba(255,255,255,0.07)" : "rgba(0,0,0,0.08)" }}>
          <img
            src="https://public.readdy.ai/ai/img_res/83fc83b2-e53d-4b28-ae49-56c4b15eb9d6.png"
            alt="Trackly"
            className={`h-10 w-auto object-contain transition-all duration-300 ${!isDark ? "invert" : ""}`}
          />
          <p className={`font-inter text-sm text-center max-w-xs leading-relaxed ${isDark ? "text-[#555555]" : "text-[#888888]"}`}>
            Take your flip tracking on the go. Trackly is coming to mobile.
          </p>
          <div className="flex items-center gap-3">
            {/* App Store Badge */}
            <a
              href="#"
              rel="nofollow"
              className={`flex items-center gap-3 px-5 py-3 rounded-xl border transition-all duration-200 cursor-pointer whitespace-nowrap hover:scale-105 ${
                isDark
                  ? "bg-white/6 border-white/10 hover:bg-white/10 hover:border-white/20"
                  : "bg-black/5 border-black/10 hover:bg-black/8 hover:border-black/18"
              }`}
            >
              <div className={`w-5 h-5 flex items-center justify-center flex-shrink-0 ${isDark ? "text-white" : "text-[#111111]"}`}>
                <i className="ri-apple-fill text-xl" />
              </div>
              <div className="text-left">
                <p className={`font-inter text-[9px] uppercase tracking-widest leading-none mb-0.5 ${isDark ? "text-[#888888]" : "text-[#888888]"}`}>
                  Download on the
                </p>
                <p className={`font-syne font-semibold text-sm leading-none ${isDark ? "text-white" : "text-[#111111]"}`}>
                  App Store
                </p>
              </div>
            </a>

            {/* Google Play Badge */}
            <a
              href="#"
              rel="nofollow"
              className={`flex items-center gap-3 px-5 py-3 rounded-xl border transition-all duration-200 cursor-pointer whitespace-nowrap hover:scale-105 ${
                isDark
                  ? "bg-white/6 border-white/10 hover:bg-white/10 hover:border-white/20"
                  : "bg-black/5 border-black/10 hover:bg-black/8 hover:border-black/18"
              }`}
            >
              <div className={`w-5 h-5 flex items-center justify-center flex-shrink-0 ${isDark ? "text-white" : "text-[#111111]"}`}>
                <i className="ri-google-play-fill text-xl" />
              </div>
              <div className="text-left">
                <p className={`font-inter text-[9px] uppercase tracking-widest leading-none mb-0.5 ${isDark ? "text-[#888888]" : "text-[#888888]"}`}>
                  Get it on
                </p>
                <p className={`font-syne font-semibold text-sm leading-none ${isDark ? "text-white" : "text-[#111111]"}`}>
                  Google Play
                </p>
              </div>
            </a>
          </div>
        </div>

      </div>
    </section>
  );
}
